import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { emitter } from '@/plugins/emitter'
import { useAuth } from '@/composables/useAuth'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import type { RirekishoFormatData } from '@/composables/format-edit/useRirekishoFormatData'

type MatchingCompany = {
  company_uid?: string
  company_name?: string
  company_address1?: string
  company_logo?: string
  terms_url?: string
  privacy_url?: string
  category?: string
}

type AiAxiosVm = {
  $axios: {
    post: (
      url: string,
      data: FormData,
      config?: { timeout: number }
    ) => Promise<{
      data: {
        success?: boolean
        message?: string
        matching_company?: MatchingCompany
        is_matching?: boolean
      }
    }>
  }
  $site: { appFlag: string }
  $deviceLib: { isMobileDevice: boolean }
  $yagiprimePaymentService: {
    init?: (vm: unknown) => void
    cleanup?: (vm: unknown) => void
    useFeature: (
      vm: unknown,
      feature: string,
      hooks: {
        onSuccess: () => void
        onAlreadyPremium?: () => void
        onCancel: () => void
      }
    ) => void
  }
}

export type UseRirekishoAiReasonOptions = {
  /** 指定時は生成前に `$yagiprimePaymentService.useFeature` を通す（Ai career 用） */
  gateFeature?: string
}

function errorMessage(error: unknown): string | undefined {
  const err = error as { response?: { data?: { message?: string } } }
  return err.response && err.response.data && err.response.data.message
}

export function useRirekishoAiReason(
  formatData: RirekishoFormatData,
  options: UseRirekishoAiReasonOptions = {}
) {
  const { vm, message, varuna, globalVar, site, deviceLib } = useHostServices()
  const host = vm as unknown as AiAxiosVm
  const { user, isAuthenticated } = useAuth()
  const gateFeature = options.gateFeature

  const fullscreenLoading = ref(false)
  const loadingText = ref('AIがあなたに最適なテキストを生成しています')
  const aiResultView = ref(false)
  const aiResultMyprResult = ref('')
  const aiResultReasonResult = ref('')
  const viewYagiprimepayment = ref(false)
  const completeBuyYagiprime = ref(false)
  const matchingCompany = ref<MatchingCompany>({})
  const isMatching = ref(false)

  const getLicences = computed(() => {
    let licensesStr = ''
    for (const key in formatData.licenseHistories) {
      licensesStr =
        licensesStr + formatData.licenseHistories[key].description + ','
    }
    return licensesStr
  })

  function loadingOn() {
    fullscreenLoading.value = true
  }

  function loadingOff() {
    fullscreenLoading.value = false
    completeBuyYagiprime.value = true
  }

  function changeLoadingText(text: unknown) {
    loadingText.value = typeof text === 'string' ? text : String(text ?? '')
  }

  function viewPaymentYagiprime() {
    viewYagiprimepayment.value = true
  }

  function skipAiContinueFree() {
    emitter.emit('go-card-to', 'output')
  }

  function getAIMyprContinue() {
    fullscreenLoading.value = true
    const body = new FormData()
    if (isAuthenticated.value && user.value?.uid) {
      body.append('uuid', user.value.uid)
    }
    body.append('education', formatData.lastestEdu)
    body.append('job', formatData.lastestJob)
    body.append('licenses', getLicences.value)
    body.append('strength', String(formatData.ai_strength || ''))
    body.append('company_name', String(formatData.ai_company_name || ''))
    body.append(
      'company_industry',
      String(formatData.hopeIndustrycategory || '')
    )
    body.append('company_job', String(formatData.hopeJobcategory || ''))

    host.$axios
      .post(varuna.endPoints.aiIntroducePr, body, { timeout: 60000 })
      .then((response) => {
        if (response.data.success) {
          aiResultMyprResult.value = response.data.message || ''
          aiResultView.value = true
          message({ message: '取得しました', type: 'success' })
        } else {
          message({ message: '失敗しました', type: 'error' })
        }
      })
      .catch((error: unknown) => {
        if (errorMessage(error) === 'AIの使用回数が上限に達しました。') {
          aiResultView.value = true
        }
        message({
          message: errorMessage(error) || '失敗しました',
          type: 'error'
        })
      })
      .finally(() => {
        fullscreenLoading.value = false
      })

    host.$axios
      .post(varuna.endPoints.aiIntroduceReason, body, { timeout: 60000 })
      .then((response) => {
        if (response.data.success) {
          aiResultReasonResult.value = response.data.message || ''
          aiResultView.value = true
          message({ message: '取得しました', type: 'success' })
        } else {
          message({ message: '失敗しました', type: 'error' })
        }
      })
      .catch((error: unknown) => {
        message({
          message: errorMessage(error) || '失敗しました',
          type: 'error'
        })
      })
      .finally(() => {
        fullscreenLoading.value = false
      })
  }

  function getAIMypr() {
    if (gateFeature) {
      host.$yagiprimePaymentService.useFeature(vm, gateFeature, {
        onSuccess: () => {
          getAIMyprContinue()
        },
        onAlreadyPremium: () => {
          getAIMyprContinue()
        },
        onCancel: () => {
          fullscreenLoading.value = false
        }
      })
      return
    }
    getAIMyprContinue()
  }

  function applyAgent() {
    if (!isAuthenticated.value || !user.value?.uid) return
    fullscreenLoading.value = true
    const body = new FormData()
    body.append('uuid', user.value.uid)
    if (matchingCompany.value) {
      body.append('company_uid', matchingCompany.value.company_uid || '')
      body.append('category', matchingCompany.value.category || '')
    }
    body.append('is_matching', String(isMatching.value))
    body.append('applicant_type_id', '2')
    body.append('app_flag', site.appFlag)
    body.append('device_flg', deviceLib.isMobileDevice ? 'SP' : 'PC')
    body.append('version', '3')
    body.append('site_flag', 'r')
    host.$axios
      .post(varuna.endPoints.applyAgent, body, { timeout: 60000 })
      .then((response) => {
        if (response.data.success) {
          message({ message: '更新完了しました', type: 'success' })
          viewYagiprimepayment.value = true
          emitter.emit('go-card-to', 'output')
        }
      })
      .catch(() => {
        message({ message: '更新失敗しました', type: 'error' })
      })
      .finally(() => {
        fullscreenLoading.value = false
      })
  }

  function useAiMyprResult() {
    formatData.reason = aiResultMyprResult.value.replace(/<br>/gi, '\n') + '\n'
  }

  function useAiReasonResult() {
    formatData.reason =
      aiResultReasonResult.value.replace(/<br>/gi, '\n') + '\n'
  }

  onBeforeMount(() => {
    emitter.on('loading_on', loadingOn)
    emitter.on('loading_off', loadingOff)
    emitter.on('change-loading-text', changeLoadingText)
    if (
      gateFeature &&
      typeof host.$yagiprimePaymentService.init === 'function'
    ) {
      host.$yagiprimePaymentService.init(vm)
    }
  })

  onBeforeUnmount(() => {
    emitter.off('loading_on', loadingOn)
    emitter.off('loading_off', loadingOff)
    emitter.off('change-loading-text', changeLoadingText)
    if (
      gateFeature &&
      typeof host.$yagiprimePaymentService.cleanup === 'function'
    ) {
      host.$yagiprimePaymentService.cleanup(vm)
    }
  })

  return {
    globalVar,
    fullscreenLoading,
    loadingText,
    aiResultView,
    aiResultMyprResult,
    aiResultReasonResult,
    viewYagiprimepayment,
    completeBuyYagiprime,
    matchingCompany,
    isMatching,
    getLicences,
    viewPaymentYagiprime,
    skipAiContinueFree,
    getAIMypr,
    applyAgent,
    useAiMyprResult,
    useAiReasonResult
  }
}
